import {
  downloadIcon,
  exportIcon,
  lockIcon,
  unlockIcon,
  trashIcon,
  fileAddIcon,
  pencilIcon
} from '@progress/kendo-svg-icons';
import { CPS_ACTIONS_ALLOWED_FOR_DOCUMENT, CpsSelectedDocument } from '@cs-cms/types';
import { ACTION_GROUPS, ActionItemId, DropDownButtonItem } from './FilterActions.types';
import { anyDocumentHasPermission, documentHasPermission, TranslateFn } from './filterActionsHelpers';

interface BuildFilterActionItemsParams {
  translate: TranslateFn;
  isAddHidden: boolean;
  canExportCsv: boolean;
  selectedDocuments: CpsSelectedDocument[];
}

function createDocumentSelectionItems(
  translate: TranslateFn,
  selectedDocuments: CpsSelectedDocument[]
): DropDownButtonItem[] {
  const isSingleSelection = selectedDocuments.length === 1;
  const downloadTextKey = isSingleSelection
    ? 'custom.toolbar.actions.download.downloadFile'
    : 'custom.toolbar.actions.download.downloadZip';

  const editItem: DropDownButtonItem | undefined = isSingleSelection
    ? {
        id: ActionItemId.EDIT,
        text: translate('custom.toolbar.actions.form.editTitle'),
        svgIcon: pencilIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: !documentHasPermission(selectedDocuments[0], CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.UPDATE)
      }
    : undefined;

  return [
    ...(editItem ? [editItem] : []),
    {
      id: ActionItemId.DELETE,
      text: translate('custom.toolbar.actions.delete.title'),
      svgIcon: trashIcon,
      groupId: ACTION_GROUPS.DOCUMENT_GROUP,
      isHidden: !anyDocumentHasPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DELETE)
    },
    {
      id: ActionItemId.UNLOCK,
      text: translate('custom.toolbar.actions.lock.unlockTitle'),
      svgIcon: unlockIcon,
      groupId: ACTION_GROUPS.DOCUMENT_GROUP,
      isHidden: !anyDocumentHasPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
    },
    {
      id: ActionItemId.LOCK,
      text: translate('custom.toolbar.actions.lock.lockTitle'),
      svgIcon: lockIcon,
      groupId: ACTION_GROUPS.DOCUMENT_GROUP,
      isHidden: !anyDocumentHasPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
    },
    {
      id: ActionItemId.DOWNLOAD,
      text: translate(downloadTextKey),
      svgIcon: downloadIcon,
      groupId: ACTION_GROUPS.DOCUMENT_GROUP,
      isHidden: !anyDocumentHasPermission(selectedDocuments, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
    }
  ];
}

function filterVisibleItems(result: DropDownButtonItem[]): DropDownButtonItem[] {
  const groupsWithVisibleItems = new Set<string>();
  result.forEach(item => {
    if (item.id !== ActionItemId.GROUP && !item.isHidden && item.groupId) {
      groupsWithVisibleItems.add(item.groupId);
    }
  });

  return result.filter(item => {
    if (item.id === ActionItemId.GROUP) {
      return Boolean(item.groupId && groupsWithVisibleItems.has(item.groupId));
    }
    return !item.isHidden;
  });
}

export function buildFilterActionItems(params: BuildFilterActionItemsParams): DropDownButtonItem[] {
  const { translate, isAddHidden, canExportCsv, selectedDocuments } = params;

  const baseItems: DropDownButtonItem[] = [
    {
      id: ActionItemId.GROUP,
      text: translate('custom.toolbar.actions.documentGroup'),
      className: 'filterActionButtonGroupOption',
      groupId: ACTION_GROUPS.DOCUMENT_GROUP
    },
    {
      id: ActionItemId.ADD_NEW,
      text: translate('custom.toolbar.actions.form.addNewTitle'),
      svgIcon: fileAddIcon,
      groupId: ACTION_GROUPS.DOCUMENT_GROUP,
      isHidden: isAddHidden
    },
    {
      id: ActionItemId.GROUP,
      text: translate('custom.toolbar.actions.gridGroup'),
      className: 'filterActionButtonGroupOption',
      groupId: ACTION_GROUPS.GRID_GROUP
    },
    {
      id: ActionItemId.EXPORT,
      text: translate('custom.toolbar.actions.export.action'),
      svgIcon: exportIcon,
      groupId: ACTION_GROUPS.GRID_GROUP,
      isHidden: !canExportCsv
    }
  ];

  const documentItems = selectedDocuments.length > 0 ? createDocumentSelectionItems(translate, selectedDocuments) : [];

  const mergedItems = [...baseItems.slice(0, 2), ...documentItems, ...baseItems.slice(2)];
  const filtered = filterVisibleItems(mergedItems);

  if (filtered.length === 0) {
    return [
      {
        id: ActionItemId.NO_ACTIONS,
        text: translate('custom.toolbar.actions.noActions') || 'No actions available'
      }
    ];
  }

  return filtered;
}
